import { APP_ENVIRONMENT } from '../../types';
import { ErrorHandler, Injectable, Provider, inject } from '@angular/core';
import { ErrorsService } from '../../providers/error/error.service';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageBase } from '../../providers/message-base/message-base';
import { SentryErrorHandler, createErrorHandler } from '@sentry/angular';
import { TelemetryService } from '../../providers/telemetry-service/telemetry-service';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';

@Injectable()
export class CustomErrorHandler extends SentryErrorHandler implements ErrorHandler {
  private readonly environment = inject(APP_ENVIRONMENT);
  private readonly errorsService: ErrorsService = inject(ErrorsService);
  private readonly message: MessageBase = inject(MessageBase);
  private readonly telemetryService: TelemetryService = inject(TelemetryService);

  constructor() {
    super();
  }

  public static getProvider(sentry: boolean): Provider[] {
    let errorHandler: Provider[] = [
      {
        provide: ErrorHandler,
        useClass: CustomErrorHandler,
      },
    ];

    if (sentry) {
      const sentryProviders = [
        {
          provide: ErrorHandler,
          useValue: createErrorHandler(),
        },
        {
          provide: ErrorHandler,
          useClass: CustomErrorHandler,
        },
      ];

      errorHandler = [...errorHandler, ...sentryProviders];
    }
    return errorHandler;
  }

  public override async handleError(error: Error): Promise<void> {
    const isSentryAvailable = !!this.environment.sentry;
    if (error instanceof HttpErrorResponse) {
      if (!navigator.onLine || error.status === 0) {
        this.message.showInfo({
          message: _('server.connection.noInternetConnection'),
        });
      }
    } else {
      if (error.message?.includes('ExpressionChangedAfterItHasBeenCheckedError')) {
        return console.error(error);
      } else if (error.message) {
        this.message.showError({
          message: error.message,
        });
      } else {
        this.message.showError(_('general.text.error'));
      }
    }
    // in case sentry is available throw error for sentry to catch
    if (isSentryAvailable) {
      const userHasTelemetryEnabled = await this.telemetryService.userHasTelemetryEnabled();
      if (userHasTelemetryEnabled) {
        this.errorsService.log(error);
        super.handleError(error);
      }
      if (!userHasTelemetryEnabled) {
        setTimeout(() => {
          this.message.showInfo({
            message: _('general.errorhandler.telemetry'),
          });
        }, 6000);
      }
    }
    return this.errorsService.log(error);
  }
}
